import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import Stars from "@/components/Stars";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { CheckCircle2, Star as StarIcon } from "lucide-react";

export default function ProductReviews({ productId, avgRating = 0, reviewCount = 0, onChange }) {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState("");
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/products/${productId}/reviews`);
      setReviews(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [productId]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/products/${productId}/reviews`, { rating, title, comment });
      toast.success("Thanks for your review!");
      setShowForm(false);
      setTitle("");
      setComment("");
      setRating(5);
      await load();
      onChange && onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save review");
    } finally { setSaving(false); }
  };

  const canReview = !!user;

  return (
    <div className="mt-12" data-testid="reviews-section">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#14A085]">Buyer opinions</div>
          <h2 className="font-display text-2xl md:text-3xl text-[#0F3D2E] mt-1">Ratings &amp; Reviews</h2>
          <div className="mt-2 flex items-center gap-3">
            <Stars value={avgRating} size={18} />
            <span className="text-sm text-[#0F3D2E] font-medium">
              {avgRating ? avgRating.toFixed(1) : "No ratings yet"}
            </span>
            <span className="text-sm text-[#6E877C]">{reviewCount} review{reviewCount === 1 ? "" : "s"}</span>
          </div>
        </div>
        {canReview && (
          <Button
            onClick={() => setShowForm(!showForm)}
            className="rounded-full bg-[#0F3D2E] hover:bg-[#14A085] text-white"
            data-testid="write-review-btn"
          >
            {showForm ? "Cancel" : "Write a Review"}
          </Button>
        )}
      </div>

      {!canReview && (
        <div className="mt-4 text-sm text-[#6E877C]">
          <a href="/login" className="text-[#14A085] underline">Sign in</a> to leave a review.
        </div>
      )}

      {showForm && (
        <form onSubmit={submit} className="mt-6 rounded-xl border border-[#E2E8E5] bg-white p-5 space-y-3" data-testid="review-form">
          <div>
            <label className="text-xs text-[#3B5249]">Your rating</label>
            <div className="mt-1 flex gap-1">
              {[1,2,3,4,5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setRating(n)}
                  className="p-1"
                  aria-label={`Rate ${n} stars`}
                  data-testid={`review-star-${n}`}
                >
                  <StarIcon
                    size={26}
                    className={n <= rating ? "text-[#F5B841] fill-[#F5B841]" : "text-[#E2E8E5]"}
                  />
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs text-[#3B5249]">Title (optional)</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Great quality!" data-testid="review-title" />
          </div>
          <div>
            <label className="text-xs text-[#3B5249]">Your review</label>
            <Textarea
              required
              rows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Tell others what you liked or didn't like…"
              data-testid="review-comment"
            />
          </div>
          <Button type="submit" disabled={saving} className="rounded-full bg-[#0F3D2E] hover:bg-[#14A085] text-white" data-testid="submit-review">
            {saving ? "Posting…" : "Post Review"}
          </Button>
        </form>
      )}

      <div className="mt-6 space-y-4" data-testid="reviews-list">
        {loading ? (
          <div className="text-sm text-[#3B5249]">Loading reviews…</div>
        ) : reviews.length === 0 ? (
          <div className="rounded-xl border border-dashed border-[#E2E8E5] bg-white p-8 text-center text-[#3B5249]">
            Be the first to review this product.
          </div>
        ) : (
          reviews.map((r) => (
            <div key={r.id} className="rounded-xl border border-[#E2E8E5] bg-white p-4" data-testid={`review-${r.id}`}>
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-[#E8F5F2] text-[#0F3D2E] flex items-center justify-center font-semibold">
                  {(r.user_name || "?").charAt(0).toUpperCase()}
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium text-[#0F3D2E]">{r.user_name}</div>
                  <div className="flex items-center gap-2">
                    <Stars value={r.rating} />
                    <span className="text-xs text-[#6E877C]">{new Date(r.created_at).toLocaleDateString()}</span>
                    {r.verified_purchase && (
                      <span className="inline-flex items-center gap-1 text-xs text-[#14A085]" data-testid="verified-badge">
                        <CheckCircle2 size={12} /> Verified Purchase
                      </span>
                    )}
                  </div>
                </div>
              </div>
              {r.title && <div className="mt-2 font-medium text-[#0F3D2E]">{r.title}</div>}
              <p className="mt-1 text-sm text-[#3B5249] whitespace-pre-wrap">{r.comment}</p>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
